/**
 * Edge Configuration Sidebar - Configure loop edges. An edge leaving a data source is a
 * plain wire: how the step reads that source is chosen on its agent's variables.
 */

import React, { useState, useEffect, useMemo } from 'react';
import type { PipelineEdge, ConditionOperator, PipelineDefinition } from '../types/pipeline';
import { EdgeType } from '../types/pipeline';
import { isDataSourceNode } from '../api/client';
import { getNodeOutputSchema, getArrayFields } from '../utils/nodeOutputSchema';
import { CONDITION_OPERATORS } from '../utils/conditionOperators';
import './EdgeConfigSidebar.css';

interface EdgeConfigSidebarProps {
  edge: PipelineEdge | null;
  pipeline: PipelineDefinition | null;
  onSave: (edgeId: string, config: Partial<PipelineEdge>) => void;
  onClose: () => void;
}

export const EdgeConfigSidebar: React.FC<EdgeConfigSidebarProps> = ({ edge, pipeline, onSave, onClose }) => {
  const [edgeType, setEdgeType] = useState<EdgeType>(EdgeType.NORMAL);
  const [loopIterateOver, setLoopIterateOver] = useState('');
  const [loopFilterEnabled, setLoopFilterEnabled] = useState(false);
  const [loopFilterField, setLoopFilterField] = useState('');
  const [loopFilterOperator, setLoopFilterOperator] = useState<ConditionOperator>('equals');
  const [loopFilterValue, setLoopFilterValue] = useState('');

  // Get source node and available fields
  const sourceNode = useMemo(() => {
    if (!edge || !pipeline) return null;
    return pipeline.nodes.find(n => n.id === edge.source) || null;
  }, [edge, pipeline]);

  const targetNode = useMemo(() => {
    if (!edge || !pipeline) return null;
    return pipeline.nodes.find(n => n.id === edge.target) || null;
  }, [edge, pipeline]);

  const isDataSourceEdge = !!sourceNode && isDataSourceNode(sourceNode);
  // A step feeding a data-source node fills that input from its output at run time
  // (WorkflowInput.value_source) - configured on the data-source node, not the edge.
  const isValueSourceEdge = !isDataSourceEdge && !!targetNode && isDataSourceNode(targetNode);
  const isInfoOnlyEdge = isDataSourceEdge || isValueSourceEdge;

  const availableFields = useMemo(() => {
    if (!sourceNode) return [];
    return getNodeOutputSchema(sourceNode.task_type);
  }, [sourceNode]);

  const arrayFields = useMemo(() => getArrayFields(availableFields), [availableFields]);
  // The filter field is a per-item field name (not part of availableFields,
  // which describes the array field itself), so its type is unknown here -
  // show the full operator set rather than guessing.
  const loopFilterOperatorOptions = CONDITION_OPERATORS;

  useEffect(() => {
    if (edge) {
      setEdgeType(edge.edge_type || EdgeType.NORMAL);
      if (edge.loop_config) {
        setLoopIterateOver(edge.loop_config.iterate_over || '');
        if (edge.loop_config.filter) {
          setLoopFilterEnabled(true);
          setLoopFilterField(edge.loop_config.filter.field || '');
          setLoopFilterOperator(edge.loop_config.filter.operator || 'equals');
          setLoopFilterValue(edge.loop_config.filter.value || '');
        }
      }
    }
  }, [edge, pipeline]);

  const handleSave = () => {
    if (!edge) return;

    const config: Partial<PipelineEdge> = {
      edge_type: edgeType,
    };

    if (edgeType === 'loop') {
      config.loop_config = {
        iterate_over: loopIterateOver,
      };
      if (loopFilterEnabled) {
        config.loop_config.filter = {
          field: loopFilterField,
          operator: loopFilterOperator,
          value: loopFilterValue,
        };
      }
      config.label = `loop: ${loopIterateOver}`;
    } else {
      config.label = undefined;
    }

    onSave(edge.id, config);
    onClose();
  };

  if (!edge) return null;

  return (
    <div className={`edge-config-sidebar ${edge ? 'open' : ''}`}>
      <div className="edge-config-header">
        <h3>Configure Edge</h3>
        <button className="close-button" onClick={onClose}>
          ×
        </button>
      </div>

      <div className="edge-config-content">
        {isValueSourceEdge ? (
          <div className="form-group">
            <label>{sourceNode?.label || 'This step'} → {targetNode?.label || 'the input'}</label>
            <small className="form-help">
              This input is filled in at run time from this step's output instead of in the run dialog. The step
              must run in aggregate mode. Pick which output field to use on the data-source node.
            </small>
          </div>
        ) : isDataSourceEdge ? (
          <div className="form-group">
            <label>{sourceNode?.label || 'This source'} → {targetNode?.label || 'the step'}</label>
            <small className="form-help">
              The workflow runs once per item of this source by default. How the step reads it is chosen on its
              agent's variables: <strong>Single item</strong> (the item being processed), <strong>All items of an
              input</strong> (every item, also on a step that runs per item) or <strong>Tracker schema</strong>. A
              source the step reads only as all items / schema is used as context and isn't iterated over.
            </small>
          </div>
        ) : (
        <>
        <div className="form-group">
          <label>Edge Type</label>
          <select
            value={edgeType}
            onChange={(e) => setEdgeType(e.target.value as EdgeType)}
            className="form-select"
          >
            <option value="normal">Normal</option>
            <option value="loop">Loop</option>
          </select>
        </div>

        {edgeType === 'loop' && (
          <>
            <div className="form-section-title">Loop Configuration</div>
            {sourceNode && (
              <div className="form-info">
                <small>Source: <strong>{sourceNode.label}</strong> ({sourceNode.task_type})</small>
              </div>
            )}
            <div className="form-group">
              <label>Iterate Over (Array Field)</label>
              {arrayFields.length > 0 ? (
                <select
                  value={loopIterateOver}
                  onChange={(e) => setLoopIterateOver(e.target.value)}
                  className="form-select"
                >
                  <option value="">Select an array field...</option>
                  {arrayFields.map((field) => (
                    <option key={field.path} value={field.path}>
                      {field.label}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  value={loopIterateOver}
                  onChange={(e) => setLoopIterateOver(e.target.value)}
                  placeholder="e.g., output.items"
                  className="form-input"
                />
              )}
              <small className="form-help">
                {arrayFields.length > 0
                  ? 'Select the array field to iterate over'
                  : arrayFields.length === 0 && sourceNode
                    ? 'No array fields available from this node type'
                    : 'Path to array/collection in previous node\'s output'}
              </small>
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={loopFilterEnabled}
                  onChange={(e) => setLoopFilterEnabled(e.target.checked)}
                />
                <span>Add Filter</span>
              </label>
            </div>

            {loopFilterEnabled && (
              <>
                <div className="form-group">
                  <label>Filter Field (within each item)</label>
                  <input
                    type="text"
                    value={loopFilterField}
                    onChange={(e) => setLoopFilterField(e.target.value)}
                    placeholder="e.g., type or status"
                    className="form-input"
                  />
                  <small className="form-help">Field name within each array item</small>
                </div>

                <div className="form-group">
                  <label>Operator</label>
                  <select
                    value={loopFilterOperator}
                    onChange={(e) => setLoopFilterOperator(e.target.value as ConditionOperator)}
                    className="form-select"
                  >
                    {loopFilterOperatorOptions.map((op) => (
                      <option key={op.value} value={op.value}>{op.label}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Value</label>
                  <input
                    type="text"
                    value={loopFilterValue}
                    onChange={(e) => setLoopFilterValue(e.target.value)}
                    placeholder="Filter value"
                    className="form-input"
                  />
                </div>
              </>
            )}
          </>
        )}
        </>
        )}

        <div className="edge-config-info">
          <strong>Source:</strong> {edge.source}<br />
          <strong>Target:</strong> {edge.target}
        </div>
      </div>

      <div className="edge-config-footer">
        <button className="button-secondary" onClick={onClose}>
          {isInfoOnlyEdge ? 'Close' : 'Cancel'}
        </button>
        {!isInfoOnlyEdge && (
          <button className="button-primary" onClick={handleSave}>
            Save
          </button>
        )}
      </div>
    </div>
  );
};
